import { ALLOWED_IMAGE_TYPES, type PickedImage } from '@ogaim/core';

/**
 * Browser image picker: a hidden file input, opened on demand.
 *
 * Resolves null when the user cancels. Cancellation has no reliable event, so
 * this leans on the window regaining focus — `change` fires only on a real
 * pick, and without the focus fallback a cancelled dialog would leave the
 * caller waiting forever.
 */
export async function pickImageFromBrowser(): Promise<PickedImage | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_IMAGE_TYPES.join(',');
    input.style.display = 'none';
    document.body.appendChild(input);

    let settled = false;
    const finish = (value: PickedImage | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      window.removeEventListener('focus', onFocus);
      resolve(value);
    };

    const onFocus = () => {
      // Focus returns before `change` dispatches, so give it a moment.
      setTimeout(() => {
        if (!input.files || input.files.length === 0) finish(null);
      }, 400);
    };

    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return finish(null);
      const dims = await readDimensions(file).catch(() => undefined);
      finish({
        uri: URL.createObjectURL(file),
        contentType: file.type,
        byteSize: file.size,
        fileName: file.name,
        blob: file,
        width: dims?.width,
        height: dims?.height,
      });
    });

    window.addEventListener('focus', onFocus);
    input.click();
  });
}

/** Natural dimensions, so the message can carry width/height and avoid reflow. */
function readDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the image.'));
    };
    img.src = url;
  });
}
